import { gql } from 'apollo-angular';
import { ConsentRequest } from '../../../../interfaces/consent-request';
import ID from '../../../../types/id';
import { ConsentRequestRoleGroup } from '../../../../interfaces/consent-request-role-group';
import ConsentChecklistTask from '../../../../interfaces/consent-checklist-task';
import ConsentChecklistStatus from '../../../../interfaces/consent-checklist-status';
import { User } from '../../../../interfaces/user';
import Group from '../../../../interfaces/group';

type FetchRequestWithChecklistResponse = {
  getConsentRequest: ConsentRequest;
  getConsentRequestRoleGroups: ConsentRequestRoleGroup[];
  getConsentChecklistStatuses: ConsentChecklistStatus[];
  me: User;
  getGroups: Group[];
};
type FetchRequestWithChecklistVariables = {
  id: ID;
};

export const FETCH_REQUEST_WITH_CHECKLIST = gql<
  FetchRequestWithChecklistResponse,
  FetchRequestWithChecklistVariables
>`
  query fetchConsentRequestWithChecklist($id: ID!) {
    getConsentRequestRoleGroups {
      id
      group_id
      role_column_name
      group {
        id
        name
      }
    }
    getConsentChecklistStatuses {
      id
      checklist_status
    }
    me {
      permissionsForResource(resourceType: CONSENTCHECKLISTTASK) {
        _create
        _read
        _edit
        _delete
        authorized_fields
      }
      groupsFlattened {
        id
        name
      }
    }
    getConsentRequest(id: $id) {
      id
      consentRequestType {
        id
        request_type
      }
      consentStatus {
        id
        status
      }
      received_date
      timing_date
      details
      urgency_note
      is_urgent_flag
      assetManager {
        id
        name
      }
      dispositionAssociate {
        id
        name
      }
      investorRelations {
        id
        name
      }
      consentManager {
        id
        name
      }
      fundManager {
        id
        name
      }
      underwriter {
        id
        name
      }
      project {
        id
        name
        developer {
          id
          name
        }
        participants(withRole: ["Sponsor"]){
          id
          person {
            id
            name
          }
          role {
            name
          }
          organization {
            id
            name
          }
          sponsor {
            id
            name
            is_repeat
            nickname
          }
        }
      }
      consentChecklistTasks(
        order: [
          { column: CREATED, order: DESC }
          { column: SORT_INDEX, order: DESC }
        ]
      ) {
        id
        task
        consent_checklist_status_id
        due_date
        start_date
        past_due
        checklist_notes
        days_to_complete
        group {
          id
          name
        }
      }
    }
  }
`;
export type RefreshChecklistTasksResponse = {
  refreshChecklistTasks: ConsentChecklistTask[];
};
export type RefreshChecklistTasksVariables = {
  consent_request_id: ID;
};
export const REFRESH_CHECKLIST_TASKS = gql<
  RefreshChecklistTasksResponse,
  RefreshChecklistTasksVariables
>`
  query refreshChecklistTasks($consent_request_id: ID) {
    refreshChecklistTasks(
      consent_request_id: $consent_request_id
      order: [
        { column: CREATED, order: DESC }
        { column: SORT_INDEX, order: DESC }
      ]
    ) {
      id
      task
      consent_checklist_status_id
      due_date
      start_date
      past_due
      checklist_notes
      days_to_complete
      group {
        id
        name
      }
    }
  }
`;

export type CreateChecklistTaskInput = {
  consent_checklist_status_id: ID;
  consent_request_id: ID;
  task: string;
  start_date: string | null;
  due_date: string | null;
  days_to_complete: number | null;
  checklist_notes: string | null;
};
type CreateChecklistTaskResponse = {
  createConsentChecklistTask: ConsentChecklistTask;
};
type CreateChecklistTaskVariables = {
  input: CreateChecklistTaskInput;
};
export const CREATE_CHECKLIST_TASK = gql<
  CreateChecklistTaskResponse,
  CreateChecklistTaskVariables
>`
  mutation createChecklistTask($input: CreateConsentChecklistTaskInput!) {
    createConsentChecklistTask(input: $input) {
      id
    }
  }
`;

export type UpdateChecklistTaskInput = {
  consent_checklist_status_id: ID;
  task: string;
  start_date: string | null;
  due_date: string | null;
  days_to_complete: number | null;
  checklist_notes: string | null;
};
type UpdateChecklistTaskResponse = {
  updateConsentChecklistTask: ConsentChecklistTask;
};
type UpdateChecklistTaskVariables = {
  id: ID;
  input: UpdateChecklistTaskInput;
};
export const UPDATE_CHECKLIST_TASK = gql<
  UpdateChecklistTaskResponse,
  UpdateChecklistTaskVariables
>`
  mutation updateChecklistTask(
    $id: ID!
    $input: UpdateConsentChecklistTaskInput!
  ) {
    updateConsentChecklistTask(id: $id, input: $input) {
      id
    }
  }
`;
export const DELETE_CHECKLIST_TASK = gql`
  mutation deleteChecklistTask($id: ID!) {
    deleteConsentChecklistTask(id: $id) {
      id
    }
  }
`;
